import { GymError, isSyntheticEmail, unwrap } from '@gymcoach/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';

import { authCopy } from './copy';
import { recoveryRedirectUrl } from './recovery';

export interface SetPasswordInput {
  password: string;
  /** Asked for when someone changes their password by choice (not forced, not a reset). */
  currentPassword?: string;
  /** Needed to check the current password. */
  email?: string | null;
}

/**
 * Sets the signed-in person's password, then clears their "temporary password" flag.
 *
 *   1. (by choice only) check the current password by signing in with it again;
 *   2. supabase.auth.updateUser({ password }): Supabase Auth stores the new one;
 *   3. complete_password_change(): the database clears profiles.must_change_password,
 *      and only if the password really is no longer the temporary one.
 *
 * Step 3 runs even when step 2 says "same password", so a retry after a dropped
 * connection between 2 and 3 still finishes the job. Refusals are shown verbatim.
 */
export function useSetOwnPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ password, currentPassword, email }: SetPasswordInput): Promise<{ cleared: boolean }> => {
      if (currentPassword !== undefined) {
        if (!email) throw new GymError(authCopy.newPassword.currentWrong, { code: 'P0001' });
        const check = await supabase.auth.signInWithPassword({ email, password: currentPassword });
        if (check.error) {
          throw new GymError(authCopy.newPassword.currentWrong, {
            code: 'P0001',
            fieldErrors: { current_password: authCopy.newPassword.currentWrong },
          });
        }
      }

      const { error } = await supabase.auth.updateUser({ password });
      const samePassword = (error as { code?: string } | null)?.code === 'same_password';
      if (error && !samePassword) throw error;

      const cleared = unwrap(await supabase.rpc('complete_password_change')) === true;
      if (samePassword && !cleared) {
        throw new GymError(authCopy.newPassword.sameAsCurrent, {
          code: 'P0001',
          fieldErrors: { password: authCopy.newPassword.sameAsCurrent },
        });
      }
      // The flag lives on the profile that RequireAuth reads.
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      return { cleared };
    },
  });
}

/**
 * "Forgot password?" for staff: Supabase Auth emails a one-time link back to /login.
 * Never for a phone-only member's synthetic address (CLAUDE.md "Sign-in"): nothing is
 * ever emailed to it.
 */
export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: async (rawEmail: string) => {
      const email = rawEmail.trim().toLowerCase();
      if (isSyntheticEmail(email)) {
        throw new GymError(authCopy.forgot.phoneOnly, { code: 'P0001' });
      }
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: recoveryRedirectUrl(window.location.origin),
      });
      if (error) throw error;
      return email;
    },
  });
}

/** Turns the reset link's tokens into a session (the client does not read URLs itself). */
export function useStartRecoverySession() {
  return useMutation({
    mutationFn: async (tokens: { accessToken: string; refreshToken: string }) => {
      const { error } = await supabase.auth.setSession({
        access_token: tokens.accessToken,
        refresh_token: tokens.refreshToken,
      });
      if (error) throw error;
    },
  });
}
